import { select, call, put } from 'redux-saga/effects'
import qs from 'query-string'
import { getContractorPortalAuthToken, getAuthToken } from 'src/selectors/auth'
import { ContractorPortalLogout, Logout } from 'src/constants/actionTypes'
import _ from 'lodash'
import { logger } from 'src/utils/logger'
import { markCallApiNetworkError } from 'src/utils/errorHandlers'

const getBaseUrl = (apiService: ApiService) => {
  if (apiService === ApiService.Paraworks) {
    return process.env.REACT_APP_API_URL
  }

  return process.env.REACT_APP_API_URL
}

export interface Options {
  headers?: {
    [x: string]: string
  }
  method?: string
  body?: any
  query?: string | Record<string, Array<string> | string | number | undefined>
  returnResponse?: boolean
  rewriteUrl?: boolean
  apiService?: ApiService
  params?: Array<UrlReplaceParams>
  resetContentType?: boolean
  returnBuffer?: boolean
  returnBlob?: boolean
}

export interface UrlReplaceParams {
  field: string
  value: string
}

export enum ApiService {
  Paraworks = 'paraworks',
}

interface ApiErrorPayload {
  error?: string
  message?: string
  statusCode?: number
}

export interface CallApiResponse<P = any> {
  status: number
  ok: boolean
  payload: P
}

const MAX_NETWORK_RETRIES = 2
const CONNECTIVITY_WAIT_TIMEOUT_MS = 30000

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const isNetworkError = (err: unknown): boolean => err instanceof TypeError

const waitForConnectivity = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (navigator.onLine) {
      resolve(true)

      return
    }

    let settled = false

    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true
        window.removeEventListener('online', onOnline)
        resolve(false)
      }
    }, CONNECTIVITY_WAIT_TIMEOUT_MS)

    const onOnline = () => {
      if (!settled) {
        settled = true
        clearTimeout(timeout)
        resolve(true)
      }
    }

    window.addEventListener('online', onOnline, { once: true })
  })

export const callApi = async (
  endpoint: string,
  options: Options = {},
): Promise<CallApiResponse> => {
  const {
    headers,
    method = 'GET',
    body,
    query,
    returnResponse,
    rewriteUrl,
    apiService = ApiService.Paraworks,
    params = [],
    resetContentType,
    returnBuffer,
    returnBlob,
  } = options
  let requestHeaders: { [x: string]: string } = {}
  let requestBody
  const url = rewriteUrl ? '' : getBaseUrl(apiService)

  if (!resetContentType) {
    requestHeaders = {
      ...requestHeaders,
      'Content-Type': 'application/json',
    }
  }

  if (headers) {
    requestHeaders = { ...requestHeaders, ...headers }
  }

  if (body) {
    if (resetContentType) {
      requestBody = body
    } else {
      requestBody = JSON.stringify(body)
    }
  }

  const endpointWithParams = params.reduce(
    (acc, param) => acc.replace(param.field, param.value),
    endpoint,
  )

  let endpointWithQuery = endpointWithParams

  if (query) {
    if (typeof query === 'object') {
      endpointWithQuery += `?${qs.stringify(_.omitBy(query, _.isNil), { encode: false })}`
    } else if (typeof query === 'string') {
      endpointWithQuery += `?${query}`
    }
  }

  const fetchUrl = url + endpointWithQuery
  const fetchOptions = { method, headers: requestHeaders, body: requestBody }

  const attemptFetch = async (): Promise<CallApiResponse> => {
    const response = await fetch(fetchUrl, fetchOptions)

    const isOk = response.status >= 200 && response.status < 400

    const responseObject: { status: number; ok: boolean; payload: any } = {
      status: response.status,
      ok: isOk,
      payload: null,
    }

    if (isOk && returnBuffer) {
      responseObject.payload = await response.arrayBuffer()
    } else if (isOk && returnBlob) {
      responseObject.payload = await response.blob()
    } else if (isOk && returnResponse) {
      responseObject.payload = await response.text()
    } else {
      const bodyText = await response.text()

      if (!bodyText) {
        responseObject.payload = null
      } else {
        const contentType = response.headers.get('content-type') ?? ''
        const expectsJson = contentType.includes('json')

        if (!expectsJson) {
          responseObject.payload = bodyText
        } else {
          try {
            responseObject.payload = JSON.parse(bodyText)
          } catch (e) {
            responseObject.payload = {
              message: 'Invalid JSON response from server',
              error: String(e),
              raw: bodyText,
            }
            responseObject.ok = false
          }
        }
      }
    }

    if (!responseObject.ok) {
      logger.error(`API Error: ${method} ${endpoint} (${responseObject.status})`, null, {
        status: responseObject.status,
        endpoint,
        method,
        payload: responseObject.payload,
      })

      throw responseObject
    }

    return responseObject
  }

  let lastNetworkError: any

  for (let attempt = 0; attempt <= MAX_NETWORK_RETRIES; attempt++) {
    try {
      return await attemptFetch()
    } catch (err: any) {
      if (!isNetworkError(err)) {
        throw err
      }

      lastNetworkError = err

      if (attempt < MAX_NETWORK_RETRIES) {
        const delayMs = Math.min(1000 * 2 ** attempt, 4000)

        logger.warn(
          `Network error, retrying (${attempt + 1}/${MAX_NETWORK_RETRIES}): ${method} ${endpoint}`,
          {
            attempt: attempt + 1,
            endpoint,
            method,
            error: err.message,
          },
        )
        await delay(delayMs)
      }
    }
  }

  logger.warn(`Network error, waiting for connectivity: ${method} ${endpoint}`, {
    endpoint,
    method,
    error: lastNetworkError?.message,
  })

  const isBackOnline = await waitForConnectivity()

  if (!isBackOnline) {
    throw markCallApiNetworkError(lastNetworkError)
  }

  logger.info(`Connectivity restored, final retry: ${method} ${endpoint}`, {
    endpoint,
    method,
  })

  try {
    return await attemptFetch()
  } catch (err: any) {
    if (isNetworkError(err)) {
      throw markCallApiNetworkError(err)
    }

    throw err
  }
}

export function* handleContractorPortalApiError(error: CallApiResponse<ApiErrorPayload>) {
  if (error?.status === 401) {
    logger.error('Contractor portal session expired', null, {
      status: error.status,
      payload: error.payload,
    })

    yield put(ContractorPortalLogout.request())

    if (error?.payload?.message) {
      error.payload.message = 'Session token is expired'
    }
  }

  throw error
}

export function* callContractorPortalSecureApi(endpoint: string, options: Options = {}) {
  const authToken: string | null = yield select(getContractorPortalAuthToken)

  if (!authToken) {
    throw new Error('Unauthorized')
  }

  // @ts-ignore
  const res = yield call(
    callSecureApi,
    authToken,
    endpoint,
    options,
    handleContractorPortalApiError,
  )

  return res
}

export function* handleClientApiError(error: CallApiResponse<ApiErrorPayload>) {
  if (error?.status === 401) {
    logger.error('Client session expired', null, {
      status: error.status,
      payload: error.payload,
    })

    yield put(Logout.request())

    if (error?.payload?.message) {
      error.payload.message = 'Session token is expired'
    }
  }

  throw error
}

export function* callClientSecureApi(endpoint: string, options: Options = {}) {
  const authToken: string | null = yield select(getAuthToken)

  if (!authToken) {
    throw new Error('Unauthorized')
  }

  // @ts-ignore
  const res = yield call(callSecureApi, authToken, endpoint, options, handleClientApiError)

  return res
}

function* callSecureApi(
  authToken: string,
  endpoint: string,
  options: Options = {},
  handleApiError: any,
) {
  if (!authToken) {
    throw new Error('Unauthorized')
  }

  const serviceHeaders: { [x: string]: string } = {}

  const optionsWithCredentials = {
    ...options,
    headers: {
      ...options?.headers,
      ...serviceHeaders,
      Authorization: `Bearer ${authToken}`,
    },
  }

  try {
    // @ts-ignore
    const res = yield call(callApi, endpoint, optionsWithCredentials)

    return res
  } catch (error: any) {
    yield call(handleApiError, error)
  }
}
